'use client'

import { useContext, useState, Fragment, useMemo, useCallback } from 'react'
import {
  LayoutDashboard as DashboardIcon,
  LogOut as LogoutIcon,
  Building2 as ApartmentIcon,
  Menu as MenuIcon,
  X as CrossIcon,
  Webhook as WebhookIcon,
} from 'lucide-react'
import Link from 'next/link'
import { AuthContext } from '@/contexts/AuthContext'
import { useRouter } from 'next/navigation'
import { objectIdIsEmpty, hasSomeAccess } from '@/lib/utils/common'
import OrganizationModal from '@/components/common/modals/OrganizationModal'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/shadcn/sheet'
import { Button } from '@/components/ui/shadcn/button'
import { Separator } from '@/components/ui/shadcn/separator'

const navItems = [
  {
    name: 'Dashboard',
    icon: <DashboardIcon className="size-4" />,
    href: '/manager/dashboard',
  },
  {
    name: 'Triggers',
    icon: <WebhookIcon className="size-4" />,
    href: '/manager/triggers',
    permission: 'viewTriggers',
  },
]

export default function MobileMenu() {
  const authContext = useContext(AuthContext)
  const router = useRouter()
  const [openChooseOrg, setOpenChooseOrg] = useState(false)
  const [orgModalPage, setOrgModalPage] = useState('choose')
  const [contracted, setContracted] = useState(false)

  const logout = () => {
    authContext.signout(() => {
      router.replace('/')
    })
  }

  const noAccess = useMemo(() => {
    if (!authContext.user?.RBACProfile?.permissions) return true
    return !hasSomeAccess(authContext.user.RBACProfile.permissions)
  }, [authContext.user?.RBACProfile])

  const toggleContracted = useCallback(() => {
    setContracted(c => !c)
  }, [])

  const NavLink = ({ icon, children, onClick, href }) => {
    const classes =
      'flex w-full items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent'
    if (href) {
      return (
        <Link href={href} onClick={onClick} className={classes}>
          {icon}
          <span>{children}</span>
        </Link>
      )
    }
    return (
      <button type="button" onClick={onClick} className={classes}>
        {icon}
        <span>{children}</span>
      </button>
    )
  }

  return (
    <div className="mobile-menu">
      <div className="flex items-center justify-end border-b border-border px-2 py-2">
        <Button variant="ghost" size="icon-sm" onClick={toggleContracted}>
          <MenuIcon />
        </Button>
      </div>

      <Sheet open={contracted} onOpenChange={setContracted}>
        <SheetContent side="top" className="p-0">
          <SheetHeader className="border-b border-border px-4 py-3">
            <SheetTitle>Menu</SheetTitle>
          </SheetHeader>
          <div className="flex min-h-[50vh] flex-col">
            <nav className="flex flex-col">
              {navItems.map(item => {
                if (
                  item.permission &&
                  !authContext.user?.isAuthorized?.(item.permission)
                )
                  return null
                if (item.name !== 'Dashboard' && noAccess) return null

                return (
                  <Fragment key={item.name}>
                    {item.href ? (
                      <NavLink
                        icon={item.icon}
                        href={item.href}
                        onClick={() => setContracted(false)}
                      >
                        {item.name}
                      </NavLink>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-2.5 text-sm">
                        {item.icon}
                        <span>{item.name}</span>
                      </div>
                    )}
                    {item.sublist && (
                      <div className="flex flex-col bg-muted/40">
                        {item.sublist.map(subitem => (
                          <NavLink
                            key={subitem.name}
                            icon={subitem.icon}
                            href={subitem.href}
                            onClick={() => setContracted(false)}
                          >
                            <span className="pl-4">{subitem.name}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </Fragment>
                )
              })}
            </nav>
            <div className="flex-1" />

            {authContext.user?.organization &&
              !objectIdIsEmpty(authContext.user.organization.id) && (
                <>
                  <Separator />
                  <NavLink
                    icon={<ApartmentIcon className="size-4" />}
                    onClick={() => setOpenChooseOrg(true)}
                  >
                    {authContext.user.organization.name}
                  </NavLink>
                </>
              )}
            <Separator />
            <NavLink icon={<LogoutIcon className="size-4" />} onClick={logout}>
              Logout
            </NavLink>
            <Separator />
            <NavLink
              icon={<CrossIcon className="size-4" />}
              onClick={() => setContracted(false)}
            >
              Close Menu
            </NavLink>
          </div>
          <OrganizationModal
            open={openChooseOrg}
            setOpen={setOpenChooseOrg}
            page={orgModalPage}
            setPage={setOrgModalPage}
            startPage="choose"
          />
        </SheetContent>
      </Sheet>
    </div>
  )
}
